import { Phone, Mail, MapPin, Globe, Building2 } from "lucide-react";
import SEO from "@/components/SEO";
import QuoteForm from "@/components/forms/QuoteForm";
import { Eyebrow, GradientText } from "@/components/Brand";
import { Reveal, MaskedLines } from "@/lib/motion";
import { SITE } from "@/lib/site";
import { motion } from "framer-motion";

const DETAILS = [
  { icon: Phone, label: "Phone", value: SITE.phone, href: SITE.phoneHref, testid: "contact-phone-link" },
  { icon: Mail, label: "Email", value: SITE.email, href: SITE.emailHref, testid: "contact-email-link" },
  { icon: MapPin, label: "Address", value: `${SITE.address1}, ${SITE.address2}`, testid: "contact-address" },
  { icon: Globe, label: "Website", value: "www.airconditioningvictoria.com.au", href: SITE.domain, testid: "contact-website-link" },
  { icon: Building2, label: "Company", value: `${SITE.legalName} — ABN ${SITE.abn}`, testid: "contact-company" },
];

export default function Contact() {
  return (
    <>
      <SEO
        title="Contact Airconditioning Victoria | Free Air Conditioning Quote"
        description="Contact Airconditioning Victoria for a free air conditioning quote. Call +61 423 250 130 or enquire online about ducted, split and multi-head systems."
        path="/contact"
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-white" data-testid="contact-hero">
        <div className="mx-auto max-w-7xl px-5 py-16 md:py-24">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>
            <Eyebrow>Contact Us</Eyebrow>
          </motion.div>
          <h1 className="mt-5 max-w-3xl font-display text-4xl font-extrabold leading-[1.06] tracking-tight text-navy sm:text-5xl lg:text-6xl">
            <MaskedLines
              delay={0.15}
              lines={["Let's find the right", <GradientText key="g">system for your home.</GradientText>]}
            />
          </h1>
          <motion.p
            className="mt-6 max-w-2xl text-base leading-relaxed text-mutedblue md:text-lg"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            Tell us about your property and the type of air conditioning you're considering. Our
            team can discuss suitable options and arrange a quotation.
          </motion.p>
        </div>
      </section>

      {/* DETAILS + FORM */}
      <section className="bg-offwhite py-20 md:py-28" data-testid="contact-main-section">
        <div className="mx-auto grid max-w-7xl items-start gap-12 px-5 lg:grid-cols-[1fr_1.3fr]">
          <div className="space-y-4">
            {DETAILS.map((d, i) => {
              const inner = (
                <>
                  <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-ice">
                    <d.icon className="h-5 w-5 text-brand-blue" />
                  </span>
                  <span>
                    <span className="block font-display text-xs font-bold uppercase tracking-[0.15em] text-navy">
                      {d.label}
                    </span>
                    <span className="mt-1 block break-words text-sm font-medium text-ink">{d.value}</span>
                  </span>
                </>
              );
              return (
                <Reveal key={d.label} delay={i * 0.06}>
                  {d.href ? (
                    <a
                      data-testid={d.testid}
                      href={d.href}
                      className="flex items-center gap-4 rounded-[16px] border border-cardline bg-white p-5 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-brand-cyan"
                    >
                      {inner}
                    </a>
                  ) : (
                    <div data-testid={d.testid} className="flex items-center gap-4 rounded-[16px] border border-cardline bg-white p-5">
                      {inner}
                    </div>
                  )}
                </Reveal>
              );
            })}
            <Reveal delay={0.3}>
              <div className="overflow-hidden rounded-[16px] border border-cardline" data-testid="contact-map">
                <iframe
                  title="Airconditioning Victoria location map — Narre Warren VIC"
                  src="https://www.google.com/maps?q=5%2F4%20Vesper%20Drive%2C%20Narre%20Warren%20VIC%203805&output=embed"
                  className="h-64 w-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <div className="rounded-[20px] border border-cardline bg-white p-7 shadow-[0_16px_50px_rgba(5,59,117,0.07)] md:p-10">
              <h2 className="font-display text-2xl font-extrabold tracking-tight text-navy">
                Request your free quote.
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-mutedblue">
                Fields marked * are required. We usually respond within one business day.
              </p>
              <div className="mt-8">
                <QuoteForm />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
